import { useTranslation } from 'react-i18next';
import { presentRetentionStatus } from '@/application/metrics/pvpRetentionPresentation';
import { LoadingBlock } from '@/presentation/components/common/LoadingBlock';
import { formatInteger, formatPercent } from '@/presentation/components/metrics/metricFormatters';
import { OwnerStatusBadge } from '@/presentation/components/metrics/OwnerStatusBadge';
import { formatMoneyCompact, formatSigned } from '@/presentation/utils/metricDeltas';
import type { ClinicRetentionSummary } from '@/application/metrics';

export type RetentionStatus = ClinicRetentionSummary['status'];

export interface BranchOpsRow {
  consultorioId: number;
  name: string;
  revenue: number;
  revenueDelta: number | null;
  pvp: number | null;
  pvpDelta: number | null;
  patients: number;
  absenteeism: number;
  status: RetentionStatus;
}

export function StatusBadge({
  status,
  testId,
}: {
  status: RetentionStatus;
  testId?: string;
}) {
  const { t } = useTranslation();
  const presentation = presentRetentionStatus(status);
  return (
    <OwnerStatusBadge
      label={t(presentation.labelKey)}
      tip={t(presentation.tipKey)}
      tone={presentation.tone}
      testId={testId ?? `clinic-status-${status}`}
    />
  );
}

interface ClinicsBranchDetailProps {
  clinicRevenue: number;
  clinicPatients: number;
  locale: string;
  branches: BranchOpsRow[] | undefined;
  loading: boolean;
  error: string | null;
  colCount: number;
  onOpenClinic: () => void;
}

/** Expand desktop: sucursales de una clínica (renderAfterRow de ResponsiveDataList). */
export function ClinicsBranchDetail({
  clinicRevenue,
  clinicPatients,
  locale,
  branches,
  loading,
  error,
  colCount,
  onOpenClinic,
}: ClinicsBranchDetailProps) {
  const { t } = useTranslation();
  const sumRevenue = branches?.reduce((s, b) => s + b.revenue, 0) ?? null;
  const sumPatients = branches?.reduce((s, b) => s + b.patients, 0) ?? null;

  return (
    <tr className="border-b border-[var(--bi-line)] bg-slate-50/70 dark:bg-white/[0.03]">
      <td colSpan={colCount} className="px-3 py-2.5 pl-10">
        {loading && !branches ? (
          <LoadingBlock size="sm" minHeightClassName="min-h-[4rem]" />
        ) : error ? (
          <p className="text-xs text-red-600 dark:text-red-300">{error}</p>
        ) : !branches || branches.length === 0 ? (
          <p className="text-xs text-gray-600 dark:text-gray-300">{t('domain.clinics.noBranchDetail')}</p>
        ) : (
          <div className="overflow-x-auto">
            <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              {t('domain.clinics.branchesTitle')}
            </p>
            <table className="min-w-full text-left text-xs">
              <thead className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
                <tr>
                  <th className="pb-1.5 pr-3 font-semibold">{t('domain.clinics.branchCols.branch')}</th>
                  <th className="pb-1.5 pr-3 font-semibold tabular-nums">
                    {t('dashboard.command.table.revenue')}
                  </th>
                  <th className="pb-1.5 pr-3 font-semibold tabular-nums">
                    {t('dashboard.command.table.pvp')}
                  </th>
                  <th className="pb-1.5 pr-3 font-semibold tabular-nums">
                    {t('dashboard.command.table.patients')}
                  </th>
                  <th className="pb-1.5 pr-3 font-semibold tabular-nums">
                    {t('dashboard.command.table.absenteeism')}
                  </th>
                  <th className="pb-1.5 font-semibold">{t('dashboard.command.table.status')}</th>
                </tr>
              </thead>
              <tbody>
                {branches.map((branch) => (
                  <tr
                    key={branch.consultorioId}
                    className="border-t border-[var(--bi-line)] first:border-0"
                  >
                    <td className="py-1.5 pr-3 font-medium text-gray-900 dark:text-gray-100">{branch.name}</td>
                    <td className="py-1.5 pr-3 tabular-nums text-gray-800 dark:text-gray-200">
                      {formatMoneyCompact(locale, branch.revenue)}
                      {branch.revenueDelta !== null && (
                        <span className="ml-1 text-[10px] text-gray-500">
                          {formatSigned(locale, branch.revenueDelta)}%
                        </span>
                      )}
                    </td>
                    <td className="py-1.5 pr-3 tabular-nums text-gray-800 dark:text-gray-200">
                      {branch.pvp === null
                        ? '—'
                        : branch.pvp.toLocaleString(locale, { maximumFractionDigits: 1 })}
                      {branch.pvpDelta !== null && (
                        <span className="ml-1 text-[10px] text-gray-500">
                          {formatSigned(locale, branch.pvpDelta)}
                        </span>
                      )}
                    </td>
                    <td className="py-1.5 pr-3 tabular-nums text-gray-800 dark:text-gray-200">
                      {formatInteger(branch.patients, locale)}
                    </td>
                    <td className="py-1.5 pr-3 tabular-nums text-gray-800 dark:text-gray-200">
                      {formatPercent(branch.absenteeism, locale)}
                    </td>
                    <td className="py-1.5">
                      <StatusBadge status={branch.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {(sumRevenue !== null || sumPatients !== null) && (
              <p className="mt-1.5 text-[10px] text-gray-500 dark:text-gray-400">
                {t('domain.clinics.branchSumHint', {
                  revenue: formatMoneyCompact(locale, sumRevenue ?? 0),
                  patients: formatInteger(sumPatients ?? 0, locale),
                  clinicRevenue: formatMoneyCompact(locale, clinicRevenue),
                  clinicPatients: formatInteger(clinicPatients, locale),
                })}
              </p>
            )}
          </div>
        )}
        <button
          type="button"
          onClick={onOpenClinic}
          className="mt-2 text-xs font-semibold text-teal-700 hover:underline dark:text-teal-300"
        >
          {t('domain.clinics.openClinic')} →
        </button>
      </td>
    </tr>
  );
}
